import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { 
  Shield, Check, X, Crown, Zap, Users, FileText, Smartphone,
  RefreshCw, AlertCircle, ChevronRight, Lock, Unlock, CreditCard,
  CheckCircle, XCircle, Loader2, Calendar, XOctagon, RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Membership level colors and icons
const LEVEL_CONFIG = {
  free: { color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300', icon: Users, badge: 'secondary' },
  starter: { color: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300', icon: Zap, badge: 'default' },
  professional: { color: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300', icon: Crown, badge: 'default' },
  enterprise: { color: 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-300', icon: Shield, badge: 'default' }
};

export default function BankIDSettings() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [bankidStatus, setBankidStatus] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [personalNumber, setPersonalNumber] = useState('');
  const [linking, setLinking] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [packages, setPackages] = useState([]);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statusRes, membershipsRes, currentRes, packagesRes, subStatusRes] = await Promise.all([
        fetch(`${API_BASE}/api/bankid/status`, { headers }),
        fetch(`${API_BASE}/api/bankid/memberships`, { headers }),
        fetch(`${API_BASE}/api/bankid/membership/current`, { headers }),
        fetch(`${API_BASE}/api/payments/packages`, { headers }),
        fetch(`${API_BASE}/api/payments/subscription/status`, { headers })
      ]);

      if (statusRes.ok) setBankidStatus(await statusRes.json());
      if (membershipsRes.ok) setMemberships(await membershipsRes.json());
      if (currentRes.ok) setCurrentMembership(await currentRes.json());
      if (packagesRes.ok) {
        const data = await packagesRes.json();
        setPackages(data.packages || []);
      }
      if (subStatusRes.ok) setSubscriptionStatus(await subStatusRes.json());
    } catch (err) {
      console.error('Error fetching BankID data:', err);
      toast.error('Kunde inte hämta BankID-information');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Check for payment return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentSuccess = searchParams.get('payment_success');
    const paymentCancelled = searchParams.get('payment_cancelled');

    if (sessionId && paymentSuccess) {
      pollPaymentStatus(sessionId);
      // Clear URL params
      setSearchParams({});
    } else if (paymentCancelled) {
      toast.error('Betalning avbruten');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      setPaymentResult({ success: false, message: 'Timeout - kontrollera din e-post för bekräftelse.' });
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await fetch(`${API_BASE}/api/payments/checkout/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Kunde inte kontrollera betalningsstatus');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        setPaymentResult({ 
          success: true, 
          message: `Betalning genomförd! Din medlemskap har uppgraderats till ${data.package_name}.` 
        });
        toast.success('Betalning genomförd!');
        fetchData(); // Refresh data
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        setPaymentResult({ success: false, message: 'Betalningssessionen har gått ut. Försök igen.' });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setCheckingPayment(false);
      setPaymentResult({ success: false, message: 'Kunde inte kontrollera betalningsstatus.' });
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLinkBankID = async () => {
    if (!personalNumber || personalNumber.length < 10) {
      toast.error('Ange ett giltigt personnummer');
      return;
    }

    setLinking(true);
    try {
      const response = await fetch(`${API_BASE}/api/bankid/link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ personal_number: personalNumber })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Personnummer kopplat!');
        setPersonalNumber('');
        fetchData();
      } else {
        toast.error(data.detail || 'Kunde inte koppla personnummer');
      }
    } catch (err) {
      toast.error('Ett fel uppstod');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkBankID = async () => {
    if (!confirm('Ar du saker pa att du vill ta bort BankID-kopplingen?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/bankid/unlink`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('BankID-koppling borttagen');
        fetchData();
      } else {
        toast.error('Kunde inte ta bort koppling');
      }
    } catch (err) {
      toast.error('Ett fel uppstod');
    }
  };

  const handleUpgrade = async (level) => {
    // Find package info
    const pkg = packages.find(p => p.id === level);
    if (!pkg) {
      toast.error('Ogiltigt paket');
      return;
    }

    setUpgrading(true);
    try {
      // Use /upgrade if user has active subscription, otherwise /subscribe
      const endpoint = subscriptionStatus?.has_subscription 
        ? `${API_BASE}/api/payments/upgrade`
        : `${API_BASE}/api/payments/subscribe`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          package_id: level,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (response.ok && data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || 'Kunde inte starta betalning');
      }
    } catch (err) {
      toast.error('Ett fel uppstod vid betalning');
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Är du säker på att du vill avsluta din prenumeration? Du behåller tillgång till tjänsten fram till periodens slut.')) return;

    setCancelling(true);
    try {
      const response = await fetch(`${API_BASE}/api/payments/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'user_requested' })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Prenumerationen kommer att avslutas vid periodens slut');
        fetchData();
      } else {
        toast.error(data.detail || 'Kunde inte avsluta prenumeration');
      }
    } catch (err) {
      toast.error('Ett fel uppstod');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setCancelling(true);
    try {
      const response = await fetch(`${API_BASE}/api/payments/reactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Prenumerationen har återaktiverats!');
        fetchData();
      } else {
        toast.error(data.detail || 'Kunde inte återaktivera prenumeration');
      }
    } catch (err) {
      toast.error('Ett fel uppstod');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Shield className="w-7 h-7 text-primary" />
          BankID & Medlemskap
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Hantera BankID-inloggning och ditt medlemskap
        </p>
      </div>

      {/* Payment Status Banner */}
      {(checkingPayment || paymentResult) && (
        <Card className={`border-2 ${paymentResult?.success ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : paymentResult ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'}`}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              {checkingPayment ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="font-medium">Kontrollerar betalningsstatus...</span>
                </>
              ) : paymentResult?.success ? (
                <>
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <span className="font-medium text-green-700 dark:text-green-300">{paymentResult.message}</span>
                </>
              ) : (
                <>
                  <XCircle className="w-6 h-6 text-red-600" />
                  <span className="font-medium text-red-700 dark:text-red-300">{paymentResult?.message}</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Membership Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Ditt medlemskap</span>
            {currentMembership && (
              <Badge variant={LEVEL_CONFIG[currentMembership.level]?.badge || 'secondary'}>
                {currentMembership.name}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {currentMembership?.bankid_enabled 
              ? 'BankID-inloggning ar aktiverat for ditt medlemskap'
              : 'Uppgradera for att fa tillgang till BankID-inloggning'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {currentMembership && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${LEVEL_CONFIG[currentMembership.level]?.color || 'bg-gray-100'}`}>
                  {React.createElement(LEVEL_CONFIG[currentMembership.level]?.icon || Users, { size: 24 })}
                </div>
                <div>
                  <div className="font-semibold text-lg">{currentMembership.name}</div>
                  <div className="text-gray-500">
                    {currentMembership.price === 0 ? 'Gratis' : `${currentMembership.price} kr/man`}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <div className="text-sm text-gray-500">Max anvandare</div>
                  <div className="font-medium">
                    {currentMembership.max_users === -1 ? 'Obegransat' : currentMembership.max_users}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Fakturor/manad</div>
                  <div className="font-medium">
                    {currentMembership.max_invoices_per_month === -1 ? 'Obegransat' : currentMembership.max_invoices_per_month}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t">
                {currentMembership.bankid_enabled ? (
                  <Badge className="bg-green-100 text-green-700">
                    <Check className="w-3 h-3 mr-1" /> BankID aktiverat
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-gray-500">
                    <Lock className="w-3 h-3 mr-1" /> BankID ej tillgangligt
                  </Badge>
                )}
              </div>

              {/* Subscription Status */}
              {subscriptionStatus?.has_subscription && (
                <div className="pt-4 border-t space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {subscriptionStatus.cancel_at_period_end 
                          ? 'Avslutas: '
                          : 'Förnyas: '}
                        {subscriptionStatus.current_period_end 
                          ? new Date(subscriptionStatus.current_period_end).toLocaleDateString('sv-SE')
                          : 'Okänt'}
                      </span>
                    </div>
                    <Badge variant={subscriptionStatus.status === 'active' ? 'default' : 'destructive'}>
                      {subscriptionStatus.status === 'active' ? 'Aktiv' : 
                       subscriptionStatus.status === 'past_due' ? 'Förfallen' :
                       subscriptionStatus.status === 'cancelled' ? 'Avslutad' : subscriptionStatus.status}
                    </Badge>
                  </div>
                  
                  {/* Cancel/Reactivate Buttons */}
                  {subscriptionStatus.cancel_at_period_end ? (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={handleReactivateSubscription}
                      disabled={cancelling}
                    >
                      {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Återaktivera prenumeration
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      variant="ghost" 
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                    >
                      {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <XOctagon className="w-4 h-4 mr-2" />
                          Avsluta prenumeration
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* BankID Link Card */}
      {bankidStatus?.bankid_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              BankID-koppling
            </CardTitle>
            <CardDescription>
              Koppla ditt personnummer for att kunna logga in med BankID
            </CardDescription>
          </CardHeader>
          <CardContent>
            {bankidStatus.user_has_bankid ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-green-800">BankID kopplat</div>
                      <div className="text-sm text-green-600">
                        Personnummer: {bankidStatus.personal_number_linked}
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleUnlinkBankID}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Ta bort
                  </Button>
                </div>
                <p className="text-sm text-gray-500">
                  Du kan nu logga in med BankID istallet for e-post och losenord.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="YYYYMMDDXXXX"
                    value={personalNumber}
                    onChange={(e) => setPersonalNumber(e.target.value)}
                    className="flex-1"
                    maxLength={13}
                  />
                  <Button 
                    onClick={handleLinkBankID}
                    disabled={linking || personalNumber.length < 10}
                  >
                    {linking ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      'Koppla'
                    )}
                  </Button>
                </div>
                <p className="text-sm text-gray-500">
                  Ange ditt personnummer (12 siffror) for att aktivera BankID-inloggning.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upgrade Card (if BankID not available) */}
      {!bankidStatus?.bankid_enabled && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertCircle className="w-5 h-5" />
              Uppgradera for BankID
            </CardTitle>
            <CardDescription className="text-amber-700">
              BankID-inloggning ar tillganglig fran Professional-medlemskap
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => handleUpgrade('professional')}
              disabled={upgrading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {upgrading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Crown className="w-4 h-4 mr-2" />
              )}
              Uppgradera till Professional
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Membership Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Tillgangliga medlemskap</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {memberships.map((plan) => {
            const config = LEVEL_CONFIG[plan.level] || LEVEL_CONFIG.free;
            const isCurrent = currentMembership?.level === plan.level;
            const Icon = config.icon;
            
            return (
              <Card 
                key={plan.level}
                className={`relative ${isCurrent ? 'ring-2 ring-primary' : ''}`}
              >
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary">Nuvarande</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className={`w-12 h-12 mx-auto rounded-lg flex items-center justify-center ${config.color}`}>
                    <Icon size={24} />
                  </div>
                  <CardTitle className="mt-2">{plan.name}</CardTitle>
                  <div className="text-2xl font-bold">
                    {plan.price === 0 ? 'Gratis' : `${plan.price} kr`}
                    {plan.price > 0 && <span className="text-sm font-normal text-gray-500">/man</span>}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-4 pt-4 border-t">
                    {plan.bankid_enabled ? (
                      <Badge className="bg-green-100 text-green-700 w-full justify-center">
                        <Shield className="w-3 h-3 mr-1" /> BankID ingår
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="w-full justify-center text-gray-400">
                        <Lock className="w-3 h-3 mr-1" /> Utan BankID
                      </Badge>
                    )}
                  </div>

                  {!isCurrent && plan.level !== 'free' && (
                    <Button 
                      className="w-full mt-4"
                      variant={plan.bankid_enabled ? 'default' : 'outline'}
                      onClick={() => handleUpgrade(plan.level)}
                      disabled={upgrading}
                    >
                      {upgrading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Uppgradera till {plan.name}
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
